/**
 * Parse streamed content into segments of text and tool activity blocks.
 *
 * Tool markers from the backend look like:
 *   <!--TOOL:{"name":"web_search","status":"running","detail":"Searching..."}-->
 *   <!--TOOL:{"name":"web_search","status":"done","summary":"Found 5 results"}-->
 *
 * Legacy format (backward compat):
 *   `● tool_name`
 *
 * Returns an array of segments:
 *   [
 *     { type: 'text', content: 'Some markdown...' },
 *     { type: 'tool', tools: [{ name, status, detail?, summary? }] },
 *     { type: 'text', content: 'More markdown...' },
 *   ]
 */

const TOOL_MARKER_REGEX = /<!--TOOL:(\{.*?\})-->/g;
const LEGACY_MARKER_REGEX = /`● ([a-z_]+)`/g;

/**
 * Parse a single tool marker JSON string.
 */
function parseMarker(jsonStr) {
  try {
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

/**
 * Merge consecutive tool markers into grouped tool blocks.
 * A "running" followed by "done" for the same tool identity merges into one
 * entry showing the final status.
 *
 * Identity is (name + detail) so that per-file events like
 *   create_file detail="dashboard.html"
 *   create_file detail="analytics.html"
 * render as distinct cards (not collapsed into one by name alone). Events with
 * no detail (e.g. web_search) still merge on name + "" which preserves the
 * original behaviour.
 */
function mergeToolEvents(events) {
  const keyOf = (evt) => `${evt.name}|${evt.detail || ''}`;
  const byKey = new Map();
  const order = [];
  for (const evt of events) {
    const k = keyOf(evt);
    const existing = byKey.get(k);
    if (existing) {
      // Same identity — update status, preserve card position
      if (evt.status === 'done' || evt.status === 'error') {
        existing.status = evt.status;
        if (evt.summary !== undefined) existing.summary = evt.summary;
      }
    } else {
      const copy = { ...evt };
      byKey.set(k, copy);
      order.push(copy);
    }
  }
  return order;
}

/**
 * Parse content string into segments.
 *
 * @param {string} content - The raw streamed content (may contain tool markers)
 * @returns {Array<{type: 'text'|'tool', content?: string, tools?: Array}>}
 */
export function parseToolActivity(content) {
  if (!content) return [{ type: 'text', content: '' }];

  // Find all tool markers (new format)
  const markers = [];
  let match;

  TOOL_MARKER_REGEX.lastIndex = 0;
  while ((match = TOOL_MARKER_REGEX.exec(content)) !== null) {
    const parsed = parseMarker(match[1]);
    if (parsed) {
      markers.push({
        start: match.index,
        end: match.index + match[0].length,
        data: parsed,
      });
    }
  }

  // Also find legacy markers
  LEGACY_MARKER_REGEX.lastIndex = 0;
  while ((match = LEGACY_MARKER_REGEX.exec(content)) !== null) {
    markers.push({
      start: match.index,
      end: match.index + match[0].length,
      data: { name: match[1], status: 'running', detail: '' },
    });
  }

  if (markers.length === 0) {
    return [{ type: 'text', content }];
  }

  // Sort markers by position
  markers.sort((a, b) => a.start - b.start);

  // Build segments
  const segments = [];
  let lastEnd = 0;

  // Group consecutive markers (markers with only whitespace between them)
  let i = 0;
  while (i < markers.length) {
    // Add text before this marker group
    const textBefore = content.slice(lastEnd, markers[i].start).replace(/^\n+|\n+$/g, '');
    if (textBefore.trim()) {
      segments.push({ type: 'text', content: textBefore });
    }

    // Collect consecutive markers (adjacent or separated only by whitespace)
    const group = [markers[i].data];
    let groupEnd = markers[i].end;
    let j = i + 1;
    while (j < markers.length) {
      const between = content.slice(groupEnd, markers[j].start).trim();
      if (between === '') {
        group.push(markers[j].data);
        groupEnd = markers[j].end;
        j++;
      } else {
        break;
      }
    }

    segments.push({ type: 'tool', tools: mergeToolEvents(group) });
    lastEnd = groupEnd;
    i = j;
  }

  // Add remaining text after last marker
  const textAfter = content.slice(lastEnd).replace(/^\n+/, '');
  if (textAfter.trim()) {
    segments.push({ type: 'text', content: textAfter });
  }

  return segments;
}

/**
 * Check if content contains any tool markers.
 */
export function hasToolMarkers(content) {
  if (!content) return false;
  TOOL_MARKER_REGEX.lastIndex = 0;
  LEGACY_MARKER_REGEX.lastIndex = 0;
  return TOOL_MARKER_REGEX.test(content) || LEGACY_MARKER_REGEX.test(content);
}

export default parseToolActivity;
